import { inject, Injectable } from '@angular/core';
import {
  AuthService,
  BackendRerouteService,
  Customer,
  CustomerService,
  Order,
  OrderToBasketService,
  SelectedCustomerService,
  BackendInterceptor
} from 'gung-standard';

import { JeevesOrderActionConfigService } from 'gung-standard-jeeves';
import { ActionButton, ActionButtonConfigService, GungNotificationService } from 'gung-common';
import { first, forkJoin, map, Observable, of } from 'rxjs';
import { Router } from '@angular/router';
import { HlDisplayOrderService } from './hl-display-order.service';
import { HlDisplayOrderModalComponent } from '../components/hl-display-order-modal/hl-display-order-modal.component';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { HttpClient } from '@angular/common/http';
import { ActionType, HlDisplayMsflowArgumentService } from './hl-display-msflow-argument.service';
import { HlDisplayPortalFeaturesService, OrderSearchSettingsGroup } from './hl-display-portal-features.service';
import { PdfDownloadIndicatorService } from 'gung-dynamic-pdf';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class HlDisplayOrderActionConfigService extends JeevesOrderActionConfigService {
  base = [
    { title: 'XLSX', action: (item: Order) => this.backendRerouteService.reroute('/download/order-excel/' + item.id) },
    { title: 'PDF', action: (item: Order) => this.backendRerouteService.reroute('/download/order-pdf/' + item.id) },
    { title: 'IMAGES', action: (item: Order) => this.backendRerouteService.reroute('/download/order-zip/' + item.id) },
    { title: 'MORE_INFO', action: (item: Order) => this.router.navigate(['/orders/' + item.id]) },
    { title: 'COPY_TO_CART', action: (item: Order) => this.customerComparison(item) }
  ];

  sales = [
    {
      title: 'QUOTE_TO_ORDER',
      action: (item: Order) => {
        const updateOrder = JSON.parse(JSON.stringify(item));
        updateOrder.extra.oh = {
          ordstat: 10
        };

        // This is a bit ugly since we set it on the OH. It is required though since the endpoint only uses that
        // object and I don't want to have it in a separate request because of the chances something goes wrong.
        // We will handle it in the backend (strip it from the oh before posting into jeeves and then using the fields
        // in the stage after).
        this.authService
          .getCurrentUser()
          .pipe(first())
          .subscribe(user => {
            let portalType = 'Customer Portal';
            if (user.roles.includes('CUSTOMER_SERVICE')) {
              portalType = 'CSO Portal';
            } else if (user.roles.includes('SALES')) {
              portalType = 'Sales Portal';
            }

            updateOrder.extra.oh[this.hlDisplayMsflowDecoratorService.getArgumentKey()] =
              this.hlDisplayMsflowDecoratorService.createMsFlowArguments(ActionType.OfferToOrder, portalType);

            this.orderService.updateOrderHeader(updateOrder).subscribe(order => {
              this.router.navigate(['/orders/' + order.id]);
              this.orderService.getQuotes(true).pipe(first()).subscribe();
              this.orderService.getOpenOrders(true).pipe(first()).subscribe();

              //this.http
              //  .post('/json/order/send-confirmation', {
              //    emailRecipients: [environment.quoteConfirmationEmailAddress],
              //    orderId: order.id
              //  })
              //  .subscribe(() => {
              //    this.router.navigate(['/orders/' + order.id]);
              //    this.orderService.getQuotes(true).pipe(first()).subscribe();
              //    this.orderService.getOpenOrders(true).pipe(first()).subscribe();
              //  });
            });
          });
      }
    },
    {
      title: 'CANCEL_QUOTE',
      action: (item: Order) => {
        const updateOrder = JSON.parse(JSON.stringify(item));
        updateOrder.extra.oh = {
          ordstat: 90
        };
        this.orderService.updateOrderHeader(updateOrder).subscribe(order => {
          this.orderService.updateQuotesSubject();
        });
      }
    }
  ];

  resendMsFlowActions = [
    {
      title: 'RESEND_MS_FLOW_CONFIRMATION',
      action: (item: Order) => {
        const queuedTranslation = this.translateService.instant('MSFLOW_QUEUED');
        this.gungNotificationService.notify(queuedTranslation, queuedTranslation, 'success');
        this.orderService.resendMsFlowOrderConfirmation(item.id).subscribe({
          next: _value => {
            const sentTranslation = this.translateService.instant('MSFLOW_SENT');
            this.gungNotificationService.notify(sentTranslation, sentTranslation, 'success');
          },
          error: _err => {
            const failedTranslation = this.translateService.instant('MSFLOW_FAILED');
            this.gungNotificationService.notify(failedTranslation, failedTranslation, 'error');
          }
        });
      }
    }
  ];

  modalRef: NgbModalRef;

  private gungNotificationService: GungNotificationService = inject(GungNotificationService);
  private translateService: TranslateService = inject(TranslateService);
  private authService = inject(AuthService);
  protected orderService = inject(HlDisplayOrderService);
  protected hlDisplayMsflowDecoratorService = inject(HlDisplayMsflowArgumentService);
  protected hlDisplayPortalFeaturesService = inject(HlDisplayPortalFeaturesService);

  sendConfirmationToCustomerService() {}

  getButtons(item: Order): Observable<ActionButton<Order>[]> {
    return forkJoin({
      roles: this.authService.getRoles().pipe(first()),
      orderSearchSettings: this.hlDisplayPortalFeaturesService
        .getPortalFeatureGroup('orderSearchSettingsGroup')
        .pipe(
          first(),
          map(settingsGroup => settingsGroup as OrderSearchSettingsGroup)
        )
    }).pipe(
      map(({ roles, orderSearchSettings }) => {
        const isSales = roles.includes('SALES');
        const isManager = roles.includes('MANAGER');
        const isAllowedToResendConfirmations = isSales || roles.includes('CUSTOMER_SERVICE') || roles.includes('ADMIN');

        if (isManager && orderSearchSettings?.enableManagerActions === false) {
          return [];
        }

        // Always remove 'images' action for managers - even when they should have other actions
        const baseCopy = isManager ? this.base.filter(b => b.title !== 'IMAGES') : [...this.base];
        if (isAllowedToResendConfirmations) {
          baseCopy.push(...this.resendMsFlowActions);
        }
        if (item.extra.oh.ordstat !== 0) {
          return baseCopy;
        }
        return isSales || isManager ? [...baseCopy, ...this.sales] : baseCopy;
      })
    );
  }

  changeCustomerDialog(currentCustomer: Customer, orderCustomer: Customer, item: Order) {
    this.modalRef = this.modalService.open(HlDisplayOrderModalComponent);
    this.modalRef.componentInstance.currentCustomer = currentCustomer;
    this.modalRef.componentInstance.nextCustomer = orderCustomer;

    this.modalRef.result
      .then(
        resolve => {
          if (resolve === 'Change') {
            this.selectedCustomerService.selectCustomer(orderCustomer.id);
          }
        },
        () => {
          throw new Error('Rejected');
        }
      )
      .then(() => this.copyToCart(item))
      .catch(error => console.log(error));
  }

  copyToCart(item: Order) {
    this.orderToBasketService
      .addOrderToBasket(item.id)
      .then(() => {
        console.log('added to cart');
      })
      .catch(e => {});
  }

  customerComparison(item: Order) {
    this.selectedCustomerService
      .getSelectedCustomer()
      .pipe(first())
      .subscribe(currentCustomer => {
        this.customerService.getCustomer(item.deliveryCustomerId).subscribe(orderCustomer => {
          currentCustomer.id?.split('@')[0] === orderCustomer.id
            ? this.copyToCart(item)
            : this.changeCustomerDialog(currentCustomer, orderCustomer, item);
        });
      });
  }
}
